import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import integrationsFileImportStyles from "./IntegrationsFileImport.styles"
import { componentKey, setUploadedFile } from "./IntegrationsFileImportSlice";
import { UploadAttachment, getFormAndTaxYearList, getFormSetting, getLoadImportData } from "./IntegrationsFileImportSaga";
import SelectDropdown from "../../../../../components/dropdown/selectdropdown/SelectDropdown";
import Heading from "../../../../../components/heading/Heading";
import FileUpload from "../../../../../components/fileupload/FileUpload";
import { addNotifications } from "../../../../toaster/ToasterPortalSlice";
import useGarbageCollector from "../../../../../../libs/garbagecollector/garbageCollectorHooks";
import Button from "../../../../../components/button/Button";
import ImportDataGridView from "../../../../components/importdatagridview/ImportDataGridView";
import { FORM_IDS, INTEGRATION_CONSTANTS, INTEGRATION_LOADING_STATES, INTEGRATION_LOADING_STATES_MESSAGE_MAP, SOURCE_CONSTANTS } from "../../constants";
import { INTEGRATION_ROUTES } from "../../../../constants/constants";
import Breadcrumbs from "../../../../../components/breadcrumbs/Breadcrumbs";
import Loadable from "../../../../../components/loadable/Loadable";
import General from "../../../../../../libs/utility/General";


export default function IntegrationsFileImport({ children, source }) {
    useGarbageCollector(componentKey);

    const [taxYearOptions, setTaxYearOptions] = useState([]);
    const [formOptions, setFormOptions] = useState([]);
    const [selectedTaxYear, setSelectedTaxYear] = useState("");
    const [selectedFormName, setSelectedFormName] = useState("");
    const dispatch = useDispatch();
    const { formAndTaxYearList, uploadedFile, uploadedFilePath, importData, formSettings, isImportDataGridViewLoaded, loadingState } = useSelector(state => state[componentKey]);

    const handleFileChange = (event, isDropEvent) => {
        const file = General.handleFileUpload(event, isDropEvent);

        if (file) {
            dispatch(setUploadedFile(file));
            dispatch(UploadAttachment(file));
        }
        else {
            dispatch(addNotifications({
                heading: "File format not supported",
                message: "Invalid file format. Please upload a .xlsx or .csv file."
            }));
        }
    };

    useEffect(() => {
        dispatch(getFormAndTaxYearList());
    }, []);

    useEffect(() => {
        if (formAndTaxYearList) {
            setTaxYearOptions(formAndTaxYearList.TaxYear);
            setFormOptions(formAndTaxYearList.FormType);
        }
    }, [formAndTaxYearList]);



    const handleNextButtonClick = () => {
        if (uploadedFilePath && selectedTaxYear && selectedFormName) {

            dispatch(getLoadImportData({
                integrationImportRequestDto: {
                    TaxYear: selectedTaxYear,
                    FormName: selectedFormName,
                    Source: source,
                    uploadedFilePath: uploadedFilePath
                }
            }))
        }
        else {
            dispatch(addNotifications({
                heading: "Required fields missing",
                message: "Please choose tax year , form type and UploadedFile to proceed"
            }));
        }
    };

    useEffect(() => {
        if (importData && selectedFormName && selectedTaxYear) {
            dispatch(getFormSetting(
                {
                    formId: selectedFormName,
                    year: selectedTaxYear
                }));
        }
    }, [importData]);

    const getTextForSource = (source) => {
        switch (source) {
            case SOURCE_CONSTANTS.SAGE_SOURCE:
                return INTEGRATION_CONSTANTS.SAGEINTACCT_TEXT;
            case SOURCE_CONSTANTS.NETSUITE_SOURCE:
                return INTEGRATION_CONSTANTS.NETSOUITE_TEXT;
            default:
                return "";
        }
    };

    const breadcrumbItems = [
        { text: 'Home', link: '/' },
        { text: 'Import', link: `/${INTEGRATION_ROUTES.IMPORT}` },
        {
            text: getTextForSource(source)
        }
    ];

    return (
        <>
            <Breadcrumbs items={breadcrumbItems} />
            {(formSettings && selectedTaxYear && selectedFormName && isImportDataGridViewLoaded) ? (
                <ImportDataGridView
                    isStateFilingApplicable={false}
                    filingForCorrected={false}
                    formSettings={formSettings}
                    formName={selectedFormName}
                    formId={
                        (() => {
                            switch (selectedFormName) {
                                case INTEGRATION_CONSTANTS.NEC_FORM:
                                    return FORM_IDS.$1099NEC;
                                case INTEGRATION_CONSTANTS.MISC_FORM:
                                    return FORM_IDS.$1099MISC;
                                default:
                                    return null;
                            }
                        })()
                    }
                    taxYear={selectedTaxYear}
                />
            ) : (
                <>
                    <Loadable
                        currentState={loadingState.state}
                        loadingStates={INTEGRATION_LOADING_STATES.PAGE_LOADING}
                        messageMap={INTEGRATION_LOADING_STATES_MESSAGE_MAP}
                        isNewLoadable={true}
                    >
                        <div style={integrationsFileImportStyles.instructionsContainer}>
                            {children}
                        </div>
                        <div style={integrationsFileImportStyles.cardContainer}>
                            <div style={integrationsFileImportStyles.card}>
                                <div style={integrationsFileImportStyles.dropdownsAndUpload}>
                                    <div style={integrationsFileImportStyles.dropdownsContainer}>
                                        <Heading type="h2" customStyle={{ ...integrationsFileImportStyles.cardHeading }}>
                                            Select Your {getTextForSource(source)} File Information
                                        </Heading>
                                        <div style={integrationsFileImportStyles.dropdown}>
                                            <SelectDropdown
                                                label="Tax Year"
                                                name="taxYear"
                                                value={{ label: selectedTaxYear || '', value: selectedTaxYear || '' }}
                                                options={taxYearOptions}
                                                onSelectCb={(name, selectedOption) => setSelectedTaxYear(selectedOption.value)}
                                                dropdownMenuStyle={integrationsFileImportStyles.dropdownWidth}
                                                dropdownContainerStyles={integrationsFileImportStyles.dropdownWidth}
                                            />
                                        </div>
                                        <div style={integrationsFileImportStyles.dropdown}>
                                            <SelectDropdown
                                                label="Form Name"
                                                name="formName"
                                                value={{ label: selectedFormName || '', value: selectedFormName || '' }}
                                                options={formOptions}
                                                onSelectCb={(name, selectedOption) => setSelectedFormName(selectedOption.value)}
                                                dropdownMenuStyle={integrationsFileImportStyles.dropdownWidth}
                                                dropdownContainerStyles={integrationsFileImportStyles.dropdownWidth}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Loadable
                                currentState={loadingState.state}
                                loadingStates={INTEGRATION_LOADING_STATES.FILE_UPLOADING}
                                messageMap={INTEGRATION_LOADING_STATES_MESSAGE_MAP}
                                isNewLoadable={true}
                            >
                                <div style={integrationsFileImportStyles.uploadSectionCard}>
                                    <div style={integrationsFileImportStyles.uploadContainer}>
                                        <FileUpload
                                            uploadedFile={uploadedFile}
                                            title="Upload the file"
                                            onDropCb={(event) => handleFileChange(event, true)}
                                            onInputChangeCb={(event) => handleFileChange(event, false)}
                                            containerBodyCustomStyles={integrationsFileImportStyles.fileUpload}
                                        />
                                    </div>
                                </div>
                            </Loadable>
                        </div>
                        <div style={integrationsFileImportStyles.container}>
                            <Button onClickCb={handleNextButtonClick}>Next</Button>
                        </div>
                    </Loadable>
                </>
            )}
        </>
    )
}